import moment from "moment";

const API_FORMAT = "YYYY-MM-DD";

export const THIS_MONTH_LABEL = "This Month";

// Single source of truth for the "This Month" default used by screens that
// share ApprovalDateRangeFilter. The picker's own "This Month" preset is
// clamped to the picker's maxDate, so a default built here highlights that
// preset (rather than "Custom Range") as long as `endAt` matches how the
// screen configures the picker.
//   endAt: "monthEnd"  -> 1st .. last day of month (approvals plan ahead)
//          "today"     -> 1st .. today (reports, no future data)
//          "yesterday" -> 1st .. yesterday (reports that exclude today;
//                         on the 1st this collapses to yesterday only)
export const getThisMonthRange = ({ endAt = "monthEnd" } = {}) => {
  let end;
  if (endAt === "today") end = moment();
  else if (endAt === "yesterday") end = moment().subtract(1, "days");
  else end = moment().endOf("month");

  const start = moment().startOf("month");
  return {
    startDate: moment.min(start, end).format(API_FORMAT),
    endDate: end.format(API_FORMAT),
    label: THIS_MONTH_LABEL,
  };
};

// Names a start/end pair the same way ApprovalDateRangeFilter's preset list
// would, so a range that arrives from a URL or a deep link shows "This Month"
// (not a spelled-out "01 Sep 2026 - 30 Sep 2026", which the picker then prints
// a second time next to its own numeric dates). Anything that isn't a preset
// is "Custom Range", the same label the picker emits for a manual pick.
export const resolvePresetLabel = (startDate, endDate) => {
  const start = moment(startDate, API_FORMAT, true);
  const end = moment(endDate, API_FORMAT, true);
  if (!start.isValid() || !end.isValid()) return "Custom Range";

  const is = (rangeStart, rangeEnd) =>
    start.isSame(rangeStart, "day") && end.isSame(rangeEnd, "day");
  const today = moment();
  const yesterday = moment().subtract(1, "days");
  const monthStart = moment().startOf("month");
  const lastMonth = moment().subtract(1, "month");

  if (is(today, today)) return "Today";
  if (is(yesterday, yesterday)) return "Yesterday";
  if (is(moment().subtract(6, "days"), today)) return "Last 7 Days";
  if (is(moment().subtract(29, "days"), today)) return "Last 30 Days";
  if (is(monthStart, moment().endOf("month")) || is(monthStart, today)) {
    return THIS_MONTH_LABEL;
  }
  if (is(lastMonth.clone().startOf("month"), lastMonth.clone().endOf("month"))) {
    return "Last Month";
  }
  return "Custom Range";
};
